import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import clienteService, {
  esErrorClienteNoOperativo,
  getMensajeClienteNoOperativo,
  OPTIONS_CLIENTE_OPERATIVO,
  PARAMS_CLIENTE_OPERATIVO,
} from '../../../services/clienteService';
import { formatCuitDniDisplay } from '../../../utils/formatters';
import {
  resolverNombreCliente,
  formatDomicilioFacturacionCliente,
  labelTipoIvaCliente,
} from './clienteNombre';
import type { POSCliente } from './types';

const PAGE_SIZE = 20;

interface Props {
  disabled?: boolean;
  onClienteSelect: (cliente: POSCliente) => void;
  /** Mensaje cuando se intenta usar un cliente inactivo (409). */
  onClienteInactivo?: (mensaje: string) => void;
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';

const normCodigoTipoIva = (tipo?: string): string => {
  const k = String(tipo ?? '').trim().toUpperCase();
  if (k === 'RI' || k === 'I') return 'I';
  if (k === 'CF' || k === 'C') return 'C';
  if (k === 'MO' || k === 'M') return 'M';
  if (k === 'EX' || k === 'E') return 'E';
  if (k === 'NI' || k === 'N') return 'N';
  return k;
};

const chipColorTipoIva = (tipo?: string): ChipColor => {
  switch (normCodigoTipoIva(tipo)) {
    case 'I':
      return 'primary';
    case 'M':
      return 'warning';
    case 'E':
      return 'success';
    case 'C':
    case 'N':
      return 'secondary';
    default:
      return 'default';
  }
};

const chipColorCondVta = (cond: string): ChipColor => {
  const n = cond.replace(/\s+/g, '.').toUpperCase();
  if (n === 'CONTADO' || n === 'EFECTIVO') return 'success';
  if (n.includes('CTA')) return 'info';
  if (n === 'CONTRAREEMBOLSO') return 'warning';
  return 'secondary';
};

const chipSx = { height: 18, fontSize: '0.65rem', flexShrink: 0, fontWeight: 600 };

/** Solo dígitos y separadores típicos de CUIT/DNI (no letras). */
const esEntradaNumericaDocumento = (texto: string) => {
  const t = texto.trim();
  if (!t) return false;
  return t.replace(/[\d\s.\-/]/g, '') === '';
};

const mergeClientesUnicos = (lista: POSCliente[], agregar: POSCliente[]) => {
  const out = [...lista];
  for (const c of agregar) {
    if (!out.some((x) => x.nroCliente === c.nroCliente)) out.push(c);
  }
  return out;
};

const terminoBusquedaApi = (trimmed: string) => {
  if (esEntradaNumericaDocumento(trimmed)) {
    return trimmed.replace(/\D/g, '');
  }
  return trimmed;
};

const FacturaPOSClienteSearchBar: React.FC<Props> = ({ disabled, onClienteSelect, onClienteInactivo }) => {
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [suggestions, setSuggestions] = useState<POSCliente[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setPageNumber(1);
    setTotalCount(0);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscarClientesPorNroExacto = useCallback(async (soloDigitos: string): Promise<POSCliente | null> => {
    if (soloDigitos.length === 0 || soloDigitos.length > 8) return null;
    const nro = Number(soloDigitos);
    if (!Number.isFinite(nro) || nro <= 0) return null;
    try {
      const porNro = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
      return porNro ? (porNro as POSCliente) : null;
    } catch (err) {
      if (esErrorClienteNoOperativo(err)) {
        onClienteInactivo?.(getMensajeClienteNoOperativo(err, nro));
      }
      return null;
    }
  }, [onClienteInactivo]);

  const buscarClientes = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      resetBusqueda();
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setLoading(true);
    setPageNumber(1);

    try {
      const searchTerm = terminoBusquedaApi(trimmed);
      const res = await clienteService.getClientes({
        searchTerm,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        ...PARAMS_CLIENTE_OPERATIVO,
      });

      if (requestId !== searchRequestIdRef.current) return;

      let items = (res.items ?? []) as POSCliente[];

      if (esEntradaNumericaDocumento(trimmed)) {
        const porNro = await buscarClientesPorNroExacto(searchTerm);
        if (requestId !== searchRequestIdRef.current) return;
        if (porNro) items = mergeClientesUnicos([porNro], items);
      }

      setTotalCount(res.totalCount ?? items.length);
      setSuggestions(items);
      setOpen(items.length > 0);
      setHighlightedIndex(items.length > 0 ? 0 : -1);
      itemRefs.current = [];
    } catch {
      if (requestId !== searchRequestIdRef.current) return;
      resetBusqueda();
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [resetBusqueda, buscarClientesPorNroExacto]);

  const cargarMas = useCallback(async () => {
    const trimmed = term.trim();
    if (!trimmed || loading || loadingMore) return;
    if (suggestions.length >= totalCount) return;

    const nextPage = pageNumber + 1;
    const requestId = searchRequestIdRef.current;
    setLoadingMore(true);

    try {
      const res = await clienteService.getClientes({
        searchTerm: terminoBusquedaApi(trimmed),
        pageNumber: nextPage,
        pageSize: PAGE_SIZE,
        ...PARAMS_CLIENTE_OPERATIVO,
      });

      if (requestId !== searchRequestIdRef.current) return;

      const items = (res.items ?? []) as POSCliente[];
      if (items.length === 0) return;

      setTotalCount(res.totalCount ?? totalCount);
      setPageNumber(nextPage);
      setSuggestions((prev) => mergeClientesUnicos(prev, items));
      setOpen(true);
    } catch {
      /* mantener resultados actuales */
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [term, loading, loadingMore, suggestions.length, totalCount, pageNumber]);

  const hayMasResultados = suggestions.length < totalCount;

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el || loading || loadingMore || !hayMasResultados) return;

    const threshold = 48;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (nearBottom) void cargarMas();
  }, [loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (!open || loading || loadingMore || !hayMasResultados) return;
    const el = listScrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1) void cargarMas();
  }, [open, loading, loadingMore, hayMasResultados, suggestions.length, cargarMas]);

  useEffect(() => {
    if (highlightedIndex < 0 || loading || loadingMore || !hayMasResultados) return;
    if (highlightedIndex >= suggestions.length - 3) void cargarMas();
  }, [highlightedIndex, suggestions.length, loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarClientes(term), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarClientes]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cliente: POSCliente) => {
    onClienteSelect(cliente);
    setTerm('');
    resetBusqueda();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const cliente = suggestions[idx];
      if (cliente) handleSelect(cliente);
    }
  };

  const handleClear = () => {
    setTerm('');
    resetBusqueda();
    inputRef.current?.focus();
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder="Nº cliente, nombre, razón social, CUIT o dirección… ↑↓ y Enter"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
            </InputAdornment>
          ),
          endAdornment: term ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end" aria-label="Limpiar búsqueda">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
      />

      {open && suggestions.length > 0 && (
        <Paper
          ref={listScrollRef}
          onScroll={handleListScroll}
          elevation={6}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            maxHeight: 320,
            overflowY: 'auto',
            mt: 0.5,
            borderRadius: 2,
          }}
        >
          <List dense disablePadding>
            {suggestions.map((c, idx) => {
              const nombre = resolverNombreCliente(c);
              const doc = formatCuitDniDisplay(c.cuit ?? c.dni);
              const domicilioFacturacion = formatDomicilioFacturacionCliente(c);
              const condVta = String(c.condVta ?? '').trim();
              return (
                <ListItem key={c.nroCliente} disablePadding divider={idx < suggestions.length - 1 || loadingMore}>
                  <ListItemButton
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    selected={idx === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onClick={() => handleSelect(c)}
                    sx={{ py: 0.85, px: 1.5 }}
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 48, fontFamily: 'monospace', flexShrink: 0 }}
                          >
                            #{c.nroCliente}
                          </Typography>
                          <Typography variant="body2" noWrap flex={1}>
                            {nombre}
                          </Typography>
                          {c.tipoIva ? (
                            <Chip
                              label={labelTipoIvaCliente(c.tipoIva)}
                              size="small"
                              color={chipColorTipoIva(c.tipoIva)}
                              sx={chipSx}
                            />
                          ) : null}
                        </Box>
                      }
                      secondary={
                        doc || domicilioFacturacion || condVta ? (
                          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                            {(doc || condVta) ? (
                              <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.75}>
                                {doc ? (
                                  <Typography variant="caption" color="text.secondary" component="span">
                                    {doc}
                                  </Typography>
                                ) : null}
                                {condVta ? (
                                  <Chip
                                    label={condVta}
                                    size="small"
                                    color={chipColorCondVta(condVta)}
                                    sx={chipSx}
                                  />
                                ) : null}
                              </Stack>
                            ) : null}
                            {domicilioFacturacion ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={{ display: 'block', lineHeight: 1.25, whiteSpace: 'normal' }}
                              >
                                {domicilioFacturacion}
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : null
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {loadingMore && (
              <ListItem disablePadding>
                <Box sx={{ py: 1.25, display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <CircularProgress size={18} />
                </Box>
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default FacturaPOSClienteSearchBar;
